import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// Scroll Animation Wrapper
const FadeInSection = ({ children, delay = 0, direction = 'up' }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setVisible(true);
      });
    }, { threshold: 0.1 }); 
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  const translateClass = direction === 'left' ? '-translate-x-12' : direction === 'right' ? 'translate-x-12' : 'translate-y-12';

  return (
    <div ref={domRef} className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0 translate-x-0' : `opacity-0 ${translateClass}`}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#f8fafc] min-h-screen font-sans overflow-x-hidden">
      
      {/* 1. HERO SECTION */}
      <section className="relative h-[60vh] flex items-center justify-center bg-[#0f172a] overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=80" 
            alt="Spacezant Team" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0f172a]"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <FadeInSection>
            <div className="inline-block border border-[#e85d04]/40 bg-[#e85d04]/20 backdrop-blur-sm rounded-full px-5 py-2 text-xs font-bold text-[#e85d04] mb-6 uppercase tracking-widest">
              Our Story
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight">
              Innovating <span className="text-[#e85d04]">Global</span> Commerce.
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Spacezant is dedicated to bridging the gap between international brands and local markets through technology.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* 2. VISION & MISSION SECTION */}
      <section className="py-24 px-6 md:px-16 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <FadeInSection direction="left">
            <div className="relative">
              <div className="rounded-[3rem] overflow-hidden shadow-2xl border-8 border-slate-50">
                <img 
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80" 
                  alt="Vision" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-10 -right-10 bg-[#e85d04] p-10 rounded-full shadow-2xl hidden md:block">
                <span className="text-white font-bold text-lg uppercase tracking-tighter">Global Impact</span>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection direction="right">
            <div>
              <h4 className="text-[#e85d04] font-bold tracking-widest uppercase text-sm mb-4">Our Vision</h4>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-8">
                Collaborate, Promote & <span className="text-[#e85d04]">Excel Together.</span>
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                At Spacezant, our mission is to integrate domestic and international brands into a single, seamless ecosystem. We offer various services from food to electronics, clothes to gadgets, ensuring everything you need is on one platform.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-2">Fast Delivery</h3>
                  <p className="text-sm text-slate-500">We deliver quick and safe, ensuring trust at every step.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-2">B2B & B2C</h3>
                  <p className="text-sm text-slate-500">Comprehensive solutions for enterprises and individual customers.</p>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 3. CORE VALUES SECTION */}
      <section className="py-24 px-6 md:px-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-20">
              <h4 className="text-[#e85d04] font-bold tracking-widest uppercase text-sm mb-3">Core Values</h4>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900">What Drives Us</h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { title: "Integrity", desc: "Building trust through transparent and reliable global logistics.", icon: "🤝" },
              { title: "Innovation", desc: "Using cutting-edge technology to simplify cross-border e-commerce.", icon: "🚀" },
              { title: "Inclusion", desc: "Empowering local sellers to compete on an international stage.", icon: "🌍" }
            ].map((value, idx) => (
              <FadeInSection key={idx} delay={idx * 200}>
                <div className="bg-white p-10 rounded-[2.5rem] shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 group hover:-translate-y-2">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">{value.icon}</div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* 4. CTA SECTION */}
      <section className="py-24 px-6 md:px-16 bg-white">
        <FadeInSection>
          <div className="bg-[#0f172a] rounded-[3rem] p-10 md:p-20 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#e85d04]/20 rounded-full blur-[100px]"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-6xl font-extrabold text-white mb-8">Ready to grow with us?</h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link to="/contact">
                  <button className="bg-[#e85d04] hover:bg-[#dc2f02] text-white px-12 py-5 rounded-2xl font-bold shadow-xl transition-all active:scale-95">
                    Partner With Us
                  </button>
                </Link>
                <Link to="/careers">
                  <button className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/20 px-12 py-5 rounded-2xl font-bold transition-all">
                    Join Our Team
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </FadeInSection>
      </section>

    </div>
  );
};

export default About;